import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Badge, Button, Card, Form, Spinner } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import toast, { Toaster } from "react-hot-toast";
import dayjs from "dayjs";
import { StudentScoreRow } from "../components/StudentScoreRow";
import { StarRating } from "../components/StarRating";
import { AddStudentModal } from "../components/AddStudentModal";
import { LinkProvisionalModal } from "../components/LinkProvisionalModal";
import { usePlanning } from "../hooks/usePlannings";
import { useRoster, useAddStudent } from "../hooks/useRoster";
import { useMarkAttendance } from "../hooks/useAttendance";
import { useLevels, useSetStudentLevel } from "../../academy/hooks/useLevels";
import { useQueryClient } from "@tanstack/react-query";
import { useOfflineGradeBatch } from "../offline/useOfflineGradeBatch";
import { SyncStatus } from "../offline/SyncStatus";
import { buildBatchEntries } from "../utils/gradeCard";

export { buildBatchEntries };

function GradeCardPage() {
  const { slug, planningId } = useParams();
  const sigRef = useRef(null);
  const signatureLoadedRef = useRef(false);
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [linkTarget, setLinkTarget] = useState(null);
  const [scores, setScores] = useState({});
  const [groupParticipation, setGroupParticipation] = useState(3);
  const [savedBalances, setSavedBalances] = useState(null);
  const [isDirty, setIsDirty] = useState(false);

  const { data: planning } = usePlanning(planningId);
  const { data: roster, isLoading: rosterLoading } = useRoster(planningId);
  const { data: levels = [] } = useLevels();
  const batchMutation = useOfflineGradeBatch(planningId);
  const addStudentMutation = useAddStudent(planningId);
  const markMutation = useMarkAttendance(planningId);
  const setLevelMutation = useSetStudentLevel();
  const queryClient = useQueryClient();
  const [levelBusyId, setLevelBusyId] = useState(null);

  const handleLevelChange = (studentId, levelId) => {
    setLevelBusyId(studentId);
    setLevelMutation.mutate(
      { studentId, levelId },
      {
        onSuccess: () => {
          toast.success("Nivel actualizado.");
          queryClient.invalidateQueries({
            queryKey: ["plannings", planningId, "roster"],
          });
        },
        onError: () => toast.error("No se pudo actualizar el nivel."),
        onSettled: () => setLevelBusyId(null),
      }
    );
  };

  // Load the previously saved signature once when planning data arrives.
  useEffect(() => {
    if (!planning?.previous_signature || !sigRef.current) return;
    if (signatureLoadedRef.current) return;
    sigRef.current.fromDataURL(planning.previous_signature);
    signatureLoadedRef.current = true;
  }, [planning?.previous_signature]);

  // Any change by the teacher invalidates the old signature — clear it so they
  // must re-sign to confirm the updated state.
  const markDirty = useCallback(() => {
    if (isDirty) return;
    setIsDirty(true);
    sigRef.current?.clear();
  }, [isDirty]);

  // Mark/unmark a student present. Saving attendance immediately reveals the
  // star rating + hours for that row (the roster query refreshes on success).
  const handleTogglePresent = (studentId, present) => {
    markDirty();
    markMutation.mutate({ student: studentId, status: present });
  };

  // Hours are fixed by the class's configured duration — not editable per row.
  const defaultHours = planning?.duration != null ? Number(planning.duration) : 1;

  const presentStudents = (roster ?? []).filter(
    (e) => e.attendance_status === true
  );

  // Load group participation from the first present student's saved grade card.
  useEffect(() => {
    if (!presentStudents.length) return;
    const first = presentStudents[0];
    if (first?.saved_participation != null) {
      setGroupParticipation(first.saved_participation);
    }
  }, [roster]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-fill scores from saved grade card data; fall back to defaults if none.
  useEffect(() => {
    if (!presentStudents.length) return;
    setScores((prev) => {
      const next = { ...prev };
      presentStudents.forEach((entry) => {
        const student = entry.student ?? entry;
        if (!next[student.id]) {
          next[student.id] = {
            score: entry.saved_score ?? 3,
            // Hours always follow the class duration (not editable per student).
            hours: defaultHours,
            participation: null,
          };
        }
      });
      return next;
    });
  }, [roster]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateScore = useCallback(
    (studentId, field, value) => {
      markDirty();
      setScores((prev) => ({
        ...prev,
        [studentId]: { ...(prev[studentId] ?? {}), [field]: value },
      }));
    },
    [markDirty]
  );

  const handleSave = () => {
    if (sigRef.current?.isEmpty()) {
      toast.error("Por favor, firma antes de guardar.");
      return;
    }
    const signature = sigRef.current.toDataURL("image/png");
    const entries = buildBatchEntries(presentStudents, scores, defaultHours);

    batchMutation.mutate(
      { signature, entries, group_participation: groupParticipation },
      {
        onSuccess: (data) => {
          toast.success(`Clase guardada — ${entries.length} grade cards registrados.`);
          if (data?.balances) setSavedBalances(data.balances);
          setIsDirty(false);
        },
        onQueued: () => {
          toast.success(
            "Guardado localmente — se sincronizará al recuperar la conexión."
          );
        },
        onError: (err) => {
          const msg =
            err?.response?.data?.detail ??
            "Error al guardar la clase. Intenta nuevamente.";
          toast.error(msg);
        },
      }
    );
  };

  const topics = planning?.topics ?? [];
  const topicNames = topics.map((t) => t.name ?? t).join(", ");

  return (
    <>
      <Toaster position="top-right" />
      <div className="container-fluid px-3" style={{ maxWidth: 520 }}>
        {/* Header */}
        <div className="py-3">
          <Link
            to={`/${slug}/app/agenda/${planningId}`}
            className="text-muted small d-block mb-2"
          >
            ← Detalle de clase
          </Link>
          <div className="d-flex justify-content-between align-items-start">
            <h1 className="h5 mb-0">{topicNames || "Grade card"}</h1>
            <SyncStatus count={batchMutation.pendingCount} />
          </div>
          <p className="text-muted small mb-0">
            {dayjs(planning?.date ?? new Date()).format("ddd D MMM")}
            {planning?.place ? ` · ${planning.place}` : ""}
          </p>
          {/* Continuity summary */}
          {topics.length > 0 && (
            <div className="mt-1">
              <Badge bg="warning" text="dark" className="small">
                Recuerda revisar el panel de continuidad antes de dictar
              </Badge>
            </div>
          )}
        </div>

        {/* Student list — attendance + grading in one place */}
        <Card className="mb-3">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <strong>Estudiantes</strong>
            <span className="text-muted small">
              {presentStudents.length} / {(roster ?? []).length} presentes
            </span>
          </Card.Header>
          <Card.Body className="p-0">
            {rosterLoading && (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" />
              </div>
            )}
            {!rosterLoading && (roster ?? []).length === 0 && (
              <p className="text-muted text-center py-4 mb-0">
                No hay estudiantes inscritos en esta clase.
              </p>
            )}
            <div className="px-3">
              {(roster ?? []).map((entry) => {
                const student = entry.student ?? entry;
                const present = entry.attendance_status === true;
                return (
                  <StudentScoreRow
                    key={student.id}
                    entry={entry}
                    present={present}
                    score={scores[student.id]?.score}
                    hours={scores[student.id]?.hours}
                    defaultHours={defaultHours}
                    busy={markMutation.isPending}
                    onTogglePresent={(p) => handleTogglePresent(student.id, p)}
                    onScoreChange={(v) => updateScore(student.id, "score", v)}
                    onLink={(s) =>
                      setLinkTarget({ id: s.id, name: s.name })
                    }
                    levels={levels}
                    onLevelChange={handleLevelChange}
                    levelBusy={levelBusyId === student.id}
                  />
                );
              })}
            </div>
            <div className="px-3 py-2">
              <Button
                variant="link"
                className="p-0 text-muted"
                style={{ minHeight: 44 }}
                onClick={() => setShowAddStudent(true)}
              >
                + Agregar estudiante no registrado
              </Button>
            </div>
          </Card.Body>
        </Card>

        {/* Group participation */}
        <Card className="mb-3">
          <Card.Body>
            <Form.Label className="fw-medium mb-2">
              Participación grupal
            </Form.Label>
            <StarRating
              value={groupParticipation}
              onChange={(v) => { markDirty(); setGroupParticipation(v); }}
              ariaLabel="Participación grupal"
            />
          </Card.Body>
        </Card>

        {/* Signature */}
        <Card className="mb-3">
          <Card.Body>
            <p className="fw-medium mb-2">✍ Firma del docente</p>
            <div
              style={{
                border: "1px solid #dee2e6",
                borderRadius: 4,
                background: "#fafafa",
                touchAction: "none",
              }}
            >
              <SignatureCanvas
                ref={sigRef}
                penColor="#212529"
                canvasProps={{
                  style: { width: "100%", height: 120, display: "block" },
                  "aria-label": "Lienzo de firma del docente",
                }}
              />
            </div>
            <Button
              variant="link"
              className="p-0 text-muted small mt-1"
              onClick={() => sigRef.current?.clear()}
            >
              Borrar firma
            </Button>
          </Card.Body>
        </Card>

        {/* Offline queue notice (TASK-19) */}
        {batchMutation.pendingCount > 0 && (
          <Alert variant="info" className="mb-3">
            Tienes {batchMutation.pendingCount} clase
            {batchMutation.pendingCount > 1 ? "s" : ""} guardada
            {batchMutation.pendingCount > 1 ? "s" : ""} localmente. Se
            sincronizará{batchMutation.pendingCount > 1 ? "n" : ""}{" "}
            automáticamente al recuperar la conexión.
          </Alert>
        )}

        {/* Saldo post-save */}
        {savedBalances && (
          <Card className="mb-3 border-success">
            <Card.Body>
              <p className="text-success fw-medium mb-2">✓ Clase guardada</p>
              {savedBalances.map((b) => (
                <div
                  key={b.student}
                  className="d-flex justify-content-between small"
                >
                  <span>{b.name ?? `Estudiante ${b.student}`}</span>
                  <span className="text-muted">
                    {b.hours_balance} h restantes
                  </span>
                </div>
              ))}
            </Card.Body>
          </Card>
        )}

        {/* Save button */}
        <div className="pb-4">
          <Button
            variant="primary"
            size="lg"
            className="w-100"
            style={{ minHeight: 56 }}
            onClick={handleSave}
            disabled={batchMutation.isPending || presentStudents.length === 0}
          >
            {batchMutation.isPending ? (
              <Spinner animation="border" size="sm" />
            ) : (
              `Guardar clase (${presentStudents.length} estudiantes)`
            )}
          </Button>
        </div>
      </div>

      <AddStudentModal
        show={showAddStudent}
        onHide={() => setShowAddStudent(false)}
        onAdd={(data) =>
          addStudentMutation.mutate(data, {
            onSuccess: () => setShowAddStudent(false),
          })
        }
        isLoading={addStudentMutation.isPending}
        error={addStudentMutation.error}
      />

      <LinkProvisionalModal
        show={Boolean(linkTarget)}
        onHide={() => setLinkTarget(null)}
        planningId={planningId}
        provisional={linkTarget}
      />
    </>
  );
}

export { GradeCardPage };
